/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, August 2020
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import FlowRestarterUi from './flow-restarter-ui';
import FlowContext from './flow-context';
import ConditionEvaluator from './condition-evaluator/condition-evaluator';
import useBridge from './use-bridge-hook';

type Props = {
  restart: boolean | { delay?: number; timeout?: number };
};
const FlowRestarter = ({ restart }: Props) => {
  const delay = (typeof restart === 'object' ? restart.delay : undefined) ?? 20;
  const timeout =
    (typeof restart === 'object' ? restart.timeout : undefined) ?? 30;
  const [restarterState, setRestarterState] = useState<
    'inactive' | 'delay' | 'countdown' | 'resetting'
  >('inactive');
  const timerRef = useRef(undefined);
  const recordInteraction = useCallback(
    (e) => {
      if (restarterState === 'inactive') {
        setRestarterState('delay');
        timerRef.current = setTimeout(
          () => setRestarterState('countdown'),
          delay * 1000,
        );
      }
      if (restarterState === 'delay') {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(
          () => setRestarterState('countdown'),
          delay * 1000,
        );
      }
      if (restarterState === 'countdown' && e.type === 'keydown') {
        setRestarterState('delay');
        timerRef.current = setTimeout(
          () => setRestarterState('countdown'),
          delay * 1000,
        );
      }
    },
    [restarterState, delay],
  );
  useEffect(() => {
    document.addEventListener('mousedown', recordInteraction);
    document.addEventListener('touchstart', recordInteraction);
    document.addEventListener('keydown', recordInteraction);
    return () => {
      document.removeEventListener('keydown', recordInteraction);
      document.removeEventListener('touchstart', recordInteraction);
      document.removeEventListener('mousedown', recordInteraction);
    };
  }, [recordInteraction]);
  const handleCancel = useCallback(() => {
    // reset timer
    setRestarterState('delay');
    timerRef.current = setTimeout(
      () => setRestarterState('countdown'),
      delay * 1000,
    );
  }, [delay]);

  const bridge = useBridge();
  const form = useFormContext();

  const { definition, stateManager } = useContext(FlowContext);
  const evaluator = useMemo(
    () => new ConditionEvaluator(stateManager, bridge, definition, form),
    [stateManager, bridge, definition, form],
  );
  const handleRestart = useCallback(() => {
    setRestarterState('resetting');
    clearTimeout(timerRef.current);
    evaluator.applyActions([['Flow.Restart']]);
  }, [timerRef, setRestarterState, evaluator]);

  if (restarterState === 'countdown') {
    return (
      <FlowRestarterUi
        timeout={timeout}
        onCancel={handleCancel}
        onRestart={handleRestart}
      />
    );
  }
  return null;
};

export default FlowRestarter;
